import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Building2,
  X,
  AlertCircle,
  Loader2,
  Home,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useInvitationStore } from "@/stores/invitation/invitation-store";
import { api } from "@/services/api/client";
import {
  FOREST_GREEN,
  ERROR,
  WARNING,
  BACKGROUND,
  SHADOWS,
  MID_GRAY,
} from "@/styles/tokens";
import { TerraMatrixBrand } from "@/components/branding/TerraMatrixBrand";
import * as errorUtils from '@/utils/errors';

/**
 * Invitation details for display
 */
interface InvitationDetails {
  status?:
    | "pending"
    | "already_accepted"
    | "already_declined"
    | "expired"
    | "canceled"
    | "invalidated"
    | "removed";
  organization: {
    name: string;
    slug: string;
  };
  inviter?: {
    name: string;
  } | null;
}

type SponsorshipPreviewStatus =
  | "pending"
  | "already_accepted"
  | "already_declined"
  | "expired"
  | "canceled"
  | "invalidated"
  | "removed";

type PageState =
  | "loading"
  | "confirm"
  | "processing"
  | "success"
  | "already_declined"
  | "expired"
  | "invalid"
  | "error";

function isSponsorshipPreviewStatus(
  value: string | undefined,
): value is SponsorshipPreviewStatus {
  return (
    value === "pending" ||
    value === "already_accepted" ||
    value === "already_declined" ||
    value === "expired" ||
    value === "canceled" ||
    value === "invalidated" ||
    value === "removed"
  );
}

function getSponsorshipPreviewMessage(
  status: SponsorshipPreviewStatus,
): string | null {
  switch (status) {
    case "already_accepted":
      return "This sponsorship invitation has already been accepted.";
    case "expired":
      return "This sponsorship invitation has expired.";
    case "canceled":
      return "This sponsorship invitation was canceled.";
    case "invalidated":
    case "removed":
      return "This sponsorship invitation is no longer available.";
    default:
      return null;
  }
}

/**
 * DeclineInvitationPage
 *
 * Simple page for declining organization invitations via email link.
 * Route: /invitations/decline/:token
 *
 * States:
 * 1. Loading: Validating invitation...
 * 2. Confirm: "Are you sure you want to decline?"
 * 3. Processing: Declining...
 * 4. Success: "Invitation declined" with home link
 * 5. Error: Handle invalid/expired tokens
 */
export const DeclineInvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteKind = searchParams.get("kind");
  const isSponsorshipInvite = inviteKind === "sponsorship" || inviteKind === "onboarding-bundle";

  const declineByToken = useInvitationStore((state) => state.declineByToken);

  const [pageState, setPageState] = useState<PageState>("loading");
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Check if coming from a successful decline (redirected from accept page)
  const fromSuccess = searchParams.get("success") === "true";

  // Validate the invitation token
  const validateToken = useCallback(async () => {
    if (!token) {
      setPageState("invalid");
      setErrorMessage("No invitation token provided.");
      return;
    }

    // If coming from a successful decline, show success directly
    if (fromSuccess) {
      setPageState("success");
      return;
    }

    try {
      const data = isSponsorshipInvite
        ? await api
            .get<{
              masterName: string;
              billingOrgName: string;
              status?: SponsorshipPreviewStatus;
            }>(`/sponsorships/token/${token}/preview`)
            .then((response) => ({
              status: response.data?.status ?? "pending",
              organization: {
                name: response.data?.billingOrgName ?? "TerraMatrix",
                slug: "account",
              },
              inviter: {
                name: response.data?.masterName ?? "A TerraMatrix user",
              },
            }))
        : await api
            .get<{
              status?: "pending" | "already_accepted" | "already_declined" | "expired";
              organization: { name: string; slug: string };
              inviter?: { name: string } | null;
            }>(`/invitations/${token}`)
            .then((response) => response.data);

      if (data) {
        const sponsorshipStatus = isSponsorshipInvite &&
          isSponsorshipPreviewStatus(data.status)
          ? data.status
          : undefined;

        if (data.status === "already_declined") {
          setInvitation({
            organization: data.organization,
            inviter: data.inviter,
          });
          setPageState("already_declined");
          return;
        }

        if (sponsorshipStatus === "already_accepted") {
          setPageState("invalid");
          setErrorMessage(getSponsorshipPreviewMessage(sponsorshipStatus) ?? "");
          return;
        }

        if (data.status === "already_accepted") {
          setPageState("invalid");
          setErrorMessage("This invitation has already been accepted.");
          return;
        }

        if (sponsorshipStatus === "expired") {
          setPageState("expired");
          setErrorMessage(getSponsorshipPreviewMessage(sponsorshipStatus) ?? "");
          return;
        }

        if (data.status === "expired") {
          setPageState("expired");
          setErrorMessage("This invitation has expired.");
          return;
        }

        if (
          sponsorshipStatus === "canceled" ||
          sponsorshipStatus === "invalidated" ||
          sponsorshipStatus === "removed"
        ) {
          setPageState("invalid");
          setErrorMessage(getSponsorshipPreviewMessage(sponsorshipStatus) ?? "");
          return;
        }

        setInvitation({
          organization: data.organization,
          inviter: data.inviter,
        });
        setPageState("confirm");
      } else {
        setPageState("invalid");
        setErrorMessage("Invalid invitation link.");
      }
    } catch (error: unknown) {
      const status = errorUtils.getErrorStatus(error);
      const message = errorUtils.getApiErrorMessage(error, "");

      if (status === 410) {
        setPageState("expired");
        setErrorMessage(message || "This invitation has expired.");
      } else if (status === 404) {
        if (message.toLowerCase().includes("declined")) {
          setPageState("already_declined");
          setErrorMessage(message);
        } else {
          setPageState("invalid");
          setErrorMessage(
            message || "This invitation link is invalid or has already been used."
          );
        }
      } else {
        setPageState("error");
        setErrorMessage(message || "Failed to validate invitation.");
      }
    }
  }, [token, fromSuccess, isSponsorshipInvite]);

  // Validate on mount
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // Handle decline
  const handleDecline = async () => {
    if (!token) return;

    setPageState("processing");

    try {
      if (isSponsorshipInvite) {
        await api.post(`/sponsorships/token/${token}/decline`);
      } else {
        await declineByToken(token);
      }
      setPageState("success");

      toast.success("Invitation declined", {
        description: invitation
          ? `You have declined the invitation to ${invitation.organization.name}.`
          : "You have declined the invitation.",
      });
    } catch (error: unknown) {
      setPageState("error");
      setErrorMessage(
        errorUtils.getApiErrorMessage(
          error,
          "Failed to decline invitation. Please try again.",
        )
      );

      toast.error("Failed to decline invitation", {
        description: errorUtils.getApiErrorMessage(error, "Please try again."),
      });
    }
  };

  // Handle go back to accept page
  const handleGoBack = () => {
    if (token) {
      navigate(`/invitations/accept/${token}${isSponsorshipInvite ? `?kind=${inviteKind}` : ""}`);
    } else {
      navigate("/");
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8"
      style={{ backgroundColor: BACKGROUND.secondary }}
    >
      {/* Header with logo */}
      <div className="mb-8">
        <Link to="/">
          <TerraMatrixBrand
            markClassName="h-10 w-10"
            nameClassName="text-3xl"
          />
        </Link>
      </div>

      {/* Card container */}
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          backgroundColor: BACKGROUND.primary,
          boxShadow: SHADOWS.xl,
        }}
      >
        {/* Loading State */}
        {pageState === "loading" && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Validating Invitation</h2>
            <p className="text-muted-foreground">
              Please wait while we verify your invitation...
            </p>
          </div>
        )}

        {/* Confirm State */}
        {pageState === "confirm" && invitation && (
          <div className="p-8">
            <div className="text-center mb-6">
              <div
                className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: WARNING.bg }}
              >
                <X className="h-8 w-8" style={{ color: WARNING.DEFAULT }} />
              </div>
              <h2 className="text-xl font-semibold mb-2">Decline Invitation?</h2>
              <p className="text-muted-foreground">
                Are you sure you want to decline the invitation to join{" "}
                <strong>{invitation.organization.name}</strong>?
              </p>
            </div>

            {/* Invitation info */}
            <div
              className="p-4 rounded-lg mb-6"
              style={{ backgroundColor: BACKGROUND.secondary }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: FOREST_GREEN.faint,
                    color: FOREST_GREEN.DEFAULT,
                  }}
                >
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">{invitation.organization.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Invited by {invitation.inviter?.name ?? "A TerraMatrix user"}
                  </p>
                </div>
              </div>
            </div>

            {/* Warning message */}
            <p
              className="text-sm text-center mb-6"
              style={{ color: MID_GRAY.DEFAULT }}
            >
              If you decline, you can contact the organization administrator to
              receive a new invitation in the future.
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleGoBack}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDecline}
              >
                <X className="h-4 w-4 mr-2" />
                Decline Invitation
              </Button>
            </div>
          </div>
        )}

        {/* Processing State */}
        {pageState === "processing" && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Declining...</h2>
            <p className="text-muted-foreground">
              Please wait while we process your response...
            </p>
          </div>
        )}

        {/* Success State */}
        {pageState === "success" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: FOREST_GREEN.faint }}
            >
              <CheckCircle2
                className="h-8 w-8"
                style={{ color: FOREST_GREEN.DEFAULT }}
              />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invitation Declined</h2>
            <p className="text-muted-foreground mb-6">
              {invitation
                ? `You have declined the invitation to ${invitation.organization.name}.`
                : "You have declined the invitation."}
            </p>
            <Button variant="outline" onClick={() => navigate("/")}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Already Declined State */}
        {pageState === "already_declined" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: MID_GRAY.lighter }}
            >
              <X className="h-8 w-8" style={{ color: MID_GRAY.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Already Declined</h2>
            <p className="text-muted-foreground mb-6">
              This invitation has already been declined.
            </p>
            <Button variant="outline" onClick={() => navigate("/")}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Expired State */}
        {pageState === "expired" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: WARNING.bg }}
            >
              <AlertCircle
                className="h-8 w-8"
                style={{ color: WARNING.DEFAULT }}
              />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invitation Expired</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage || "This invitation has expired."}
            </p>
            <Button variant="outline" onClick={() => navigate("/")}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Invalid State */}
        {pageState === "invalid" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: ERROR.bg }}
            >
              <AlertCircle
                className="h-8 w-8"
                style={{ color: ERROR.DEFAULT }}
              />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage ||
                "This invitation link is invalid or has already been used."}
            </p>
            <Button variant="outline" onClick={() => navigate("/")}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Error State */}
        {pageState === "error" && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: ERROR.bg }}
            >
              <AlertCircle
                className="h-8 w-8"
                style={{ color: ERROR.DEFAULT }}
              />
            </div>
            <h2 className="text-xl font-semibold mb-2">Something Went Wrong</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage || "An error occurred. Please try again."}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate("/")}>
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
              <Button onClick={() => validateToken()}>Try Again</Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer text */}
      <p className="mt-8 text-sm text-muted-foreground text-center">
        Changed your mind?{" "}
        {token && (
          <Link
            to={`/invitations/accept/${token}${isSponsorshipInvite ? `?kind=${inviteKind}` : ""}`}
            className="text-primary hover:underline"
          >
            Accept the invitation instead
          </Link>
        )}
      </p>
    </div>
  );
};

export default DeclineInvitationPage;
